import { useEffect, useMemo, useState, type ReactNode } from 'react';

import { apiRequest } from '../../services/api';
import { joinApiUrl } from '../../utils/apiUrl';
import AcademiaSelect from '../common/AcademiaSelect';
import LucideSymbolIcon from '../icons/LucideSymbolIcon';

type VideoQuality = { label: string; storageKey?: string; height?: number; bitrateKbps?: number };

type Props = {
  ressourceId: string;
  mediaStreamPath?: string | null;
  videoQualities?: VideoQuality[] | null;
  transcodageStatut?: string | null;
  poster?: string | null;
  title: string;
};

function buildStreamUrl(path: string, mediaToken: string, quality: string) {
  const params = new URLSearchParams();
  params.set('mediaToken', mediaToken);
  if (quality && quality !== 'auto') params.set('quality', quality);
  return joinApiUrl(path, params);
}

export function ElearningVideoCardShell({
  title,
  meta = 'Lecture vidéo',
  toolbar,
  notice,
  children,
}: {
  title: string;
  meta?: string;
  toolbar?: React.ReactNode;
  notice?: { text: string; variant: 'warning' | 'error' };
  children: ReactNode;
}) {
  return (
    <section className="elearning-video-card" aria-label={title}>
      <div className="elearning-video-card__accent" aria-hidden />
      <header className="elearning-video-card__header">
        <span className="elearning-video-card__icon" aria-hidden>
          <LucideSymbolIcon name="play_circle" tone="inherit" size={22} />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="elearning-video-card__title">{title}</h2>
          <p className="elearning-video-card__meta">{meta}</p>
        </div>
        {toolbar ? <div className="elearning-video-card__toolbar">{toolbar}</div> : null}
      </header>
      {notice ? (
        <p className={`elearning-video-card__notice elearning-video-card__notice--${notice.variant}`}>
          {notice.text}
        </p>
      ) : null}
      <div className="elearning-video-card__stage">{children}</div>
    </section>
  );
}

export default function ElearningVideoPlayer({
  ressourceId,
  mediaStreamPath,
  videoQualities,
  transcodageStatut,
  poster,
  title,
}: Props) {
  const [quality, setQuality] = useState('auto');
  const [src, setSrc] = useState('');
  const [streamError, setStreamError] = useState('');
  const [playbackError, setPlaybackError] = useState('');
  const [loading, setLoading] = useState(true);
  const path = mediaStreamPath || `/elearning/ressources/${encodeURIComponent(ressourceId)}/stream`;
  const qualities = videoQualities ?? [];

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    async function loadStreamUrl() {
      setStreamError('');
      try {
        const response = await apiRequest(`/elearning/ressources/${encodeURIComponent(ressourceId)}/stream-token`, {
          method: 'GET',
          withAuth: true,
        });
        const mediaToken = response?.data?.mediaToken;
        if (typeof mediaToken !== 'string' || !mediaToken) {
          throw new Error('Jeton média indisponible');
        }
        if (!cancelled) {
          setSrc(buildStreamUrl(path, mediaToken, quality));
        }
      } catch (error) {
        if (!cancelled) {
          setSrc('');
          setStreamError(error instanceof Error ? error.message : 'Lecture vidéo impossible');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void loadStreamUrl();
    return () => {
      cancelled = true;
    };
  }, [path, quality, ressourceId]);

  const qualityToolbar = useMemo(() => {
    if (qualities.length === 0) return null;
    return (
      <>
        <span className="elearning-video-card__toolbar-label">Qualité</span>
        <AcademiaSelect
          id={`video-quality-${ressourceId}`}
          fieldSize="sm"
          value={quality}
          onChange={(e) => setQuality(e.target.value)}
          aria-label="Qualité vidéo"
        >
          <option value="auto">Automatique</option>
          {qualities.map((q) => (
            <option key={q.label} value={q.label.toLowerCase()}>
              {q.label}
            </option>
          ))}
          <option value="original">Original</option>
        </AcademiaSelect>
      </>
    );
  }, [qualities, quality, ressourceId]);

  const transcodageNotice =
    playbackError
      ? { text: playbackError, variant: 'error' as const }
      : !qualities.length &&
          transcodageStatut &&
          transcodageStatut !== 'NON_APPLICABLE' &&
          transcodageStatut !== 'TERMINE'
        ? {
            text:
              transcodageStatut === 'EN_COURS' || transcodageStatut === 'EN_ATTENTE'
                ? 'Transcodage vidéo en cours — réessayez dans quelques instants si la lecture ne démarre pas.'
                : transcodageStatut === 'ECHOUE'
                  ? 'Transcodage échoué — utilisez un fichier MP4 (H.264) pour une lecture immédiate.'
                  : `Transcodage vidéo : ${transcodageStatut}`,
            variant: 'warning' as const,
          }
        : streamError
          ? { text: streamError, variant: 'error' as const }
          : undefined;

  return (
    <ElearningVideoCardShell title={title} toolbar={qualityToolbar} notice={transcodageNotice}>
      {loading && !src ? (
        <p className="elearning-video-card__loading">Chargement de la vidéo…</p>
      ) : null}
      {src ? (
        <video
          key={src}
          className="elearning-video-card__video"
          controls
          controlsList="nodownload"
          playsInline
          preload="metadata"
          poster={poster ?? undefined}
          title={title}
          src={src}
          onLoadedData={() => setPlaybackError('')}
          onError={() =>
            setPlaybackError(
              'Impossible de lire cette vidéo dans le navigateur. Attendez la fin du transcodage ou téléversez un fichier MP4.',
            )
          }
          onContextMenu={(event) => event.preventDefault()}
          onDragStart={(event) => event.preventDefault()}
        >
          Votre navigateur ne supporte pas la lecture vidéo.
        </video>
      ) : null}
      {!loading && !src && !streamError ? (
        <p className="elearning-video-card__loading">Vidéo indisponible.</p>
      ) : null}
    </ElearningVideoCardShell>
  );
}

export async function buildElearningMediaUrl(
  path: string | null | undefined,
  ressourceId: string,
  quality?: string,
) {
  if (!path) return null;
  const response = await apiRequest(`/elearning/ressources/${encodeURIComponent(ressourceId)}/stream-token`, {
    method: 'GET',
    withAuth: true,
  });
  const mediaToken = response?.data?.mediaToken;
  if (typeof mediaToken !== 'string' || !mediaToken) {
    throw new Error('Jeton média indisponible');
  }
  const params = new URLSearchParams();
  params.set('mediaToken', mediaToken);
  if (quality && quality !== 'auto') params.set('quality', quality);
  return joinApiUrl(path, params);
}

export function useElearningMediaUrl(ressourceId: string, path: string | null | undefined) {
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    if (!path) {
      setUrl('');
      setError('');
      return () => {
        cancelled = true;
      };
    }

    void buildElearningMediaUrl(path, ressourceId)
      .then((nextUrl) => {
        if (!cancelled) {
          setUrl(nextUrl ?? '');
          setError('');
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setUrl('');
          setError(err instanceof Error ? err.message : 'Média indisponible');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [path, ressourceId]);

  return { url, error };
}
